import * as i0 from '@angular/core';
import { EventEmitter, inject, Output, Input, ViewEncapsulation, Component, ViewContainerRef, ElementRef, Inject, Directive, NgModule } from '@angular/core';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import Viewer from 'viewerjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';

class E11ImageViewerComponent {
    set imageUrl(imageUrls) {
        this._imageUrl = typeof imageUrls === 'string' ? [imageUrls] : imageUrls;
        if (this.viewer)
            this.viewer.update();
    }
    constructor() {
        this.randomId = generateRandomHTMLId('e11-image-viewer');
        this._imageUrl = [];
        this.openOnInit = false;
        this.close = new EventEmitter();
        this.isBrowser = inject(IS_BROWSER);
    }
    ngAfterViewInit() {
        const viewerHTML = document.getElementById(this.randomId);
        if (!this.isBrowser || !viewerHTML)
            return null;
        this.viewer = new Viewer(viewerHTML, {
            inline: false,
            toolbar: false,
            title: false,
            initialViewIndex: this.initialViewIndex,
            hide: (event) => {
                this.close.emit(true);
            },
        });
        if (this.openOnInit)
            this.viewer.show();
    }
    open(index) {
        if (!this.viewer)
            return;
        if (index)
            this.viewer.view(index);
        else
            this.viewer.view();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ImageViewerComponent, selector: "e11-image-viewer", inputs: { imageUrl: "imageUrl", openOnInit: "openOnInit", initialViewIndex: "initialViewIndex" }, outputs: { close: "close" }, ngImport: i0, template: `
    <div>
      <ul class="" [id]="randomId">
        <li *ngFor="let image of _imageUrl"><img class="e11-hidden" [src]="image" alt="Image" /></li>
      </ul>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-image-viewer',
                    template: `
    <div>
      <ul class="" [id]="randomId">
        <li *ngFor="let image of _imageUrl"><img class="e11-hidden" [src]="image" alt="Image" /></li>
      </ul>
    </div>
  `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [], propDecorators: { imageUrl: [{
                type: Input
            }], openOnInit: [{
                type: Input
            }], initialViewIndex: [{
                type: Input
            }], close: [{
                type: Output
            }] } });
function generateRandomHTMLId(prefix = 'e11') {
    return `${prefix}-${Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)}`;
}

class E11ImageViewerDirective {
    constructor(appRef, renderer, element) {
        this.appRef = appRef;
        this.renderer = renderer;
        this.element = element;
    }
    ngOnInit() {
        this.renderer.listen(this.element.nativeElement, 'click', () => this.createAndInsertComponent());
    }
    createAndInsertComponent() {
        // Get the root view container reference
        const rootViewContainerRef = this.appRef.components[0].injector.get(ViewContainerRef);
        const componentRef = rootViewContainerRef.createComponent(E11ImageViewerComponent);
        const e11ImageViewerComponent = componentRef.instance;
        const { ...componentInput } = this.componentInput;
        Object.assign(e11ImageViewerComponent, componentInput);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerDirective, deps: [{ token: i0.ApplicationRef }, { token: i0.Renderer2 }, { token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11ImageViewerDirective, selector: "[e11ImageViewer]", inputs: { componentInput: "componentInput" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[e11ImageViewer]' }]
        }], ctorParameters: () => [{ type: i0.ApplicationRef }, { type: i0.Renderer2 }, { type: i0.ElementRef, decorators: [{
                    type: Inject,
                    args: [ElementRef]
                }] }], propDecorators: { componentInput: [{
                type: Input
            }] } });

class E11ImageViewerModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerModule, declarations: [E11ImageViewerComponent, E11ImageViewerDirective], imports: [CommonModule], exports: [E11ImageViewerComponent, E11ImageViewerDirective, E11UtilModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerModule, imports: [CommonModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ImageViewerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11ImageViewerComponent, E11ImageViewerDirective],
                    imports: [CommonModule],
                    exports: [E11ImageViewerComponent, E11ImageViewerDirective, E11UtilModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11ImageViewerComponent, E11ImageViewerDirective, E11ImageViewerModule };
